import { keepPreviousData, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query'
import { useCallback } from 'react'
import i18n from '@rs/ui/i18n'
import { ApiError } from './api.ts'

/**
 * ══ TRẠNG THÁI MÁY CHỦ TÁCH KHỎI TRẠNG THÁI GIAO DIỆN (KIEN-TRUC §12.2) ═══════════════════════
 *
 * Trước đợt N5, mỗi màn tự viết lại đúng bộ bốn dòng này:
 *
 *     const [rows, setRows] = useState<X[] | null>(null)
 *     const [err, setErr]   = useState<string | null>(null)
 *     const reload = () => { fetchX().then(setRows, (e) => setErr(e.message)) }
 *     useEffect(reload, [])
 *
 * 22 màn, 22 bản sao. Ba hệ quả, hệ quả thứ ba mới là hệ quả nặng:
 *  ① Mở lại cùng một tab là gọi lại máy chủ từ đầu, dù dữ liệu vừa lấy xong hai giây trước.
 *  ② Hai màn cùng cần danh sách người dùng thì gọi hai lần — không có gì gộp chúng lại.
 *  ③ **Ghi xong phải NHỚ gọi `reload()`.** Quên một chỗ là màn hiện dữ liệu cũ, và người dùng
 *     bấm lưu lần nữa vì tưởng chưa lưu được. Trong danh mục kế toán, bấm lưu hai lần là hai dòng.
 *
 * `useList` giữ NGUYÊN hình dạng `{ rows, err, reload }` mà các màn đang dùng, nên chuyển sang nó
 * là đổi bốn dòng đầu chứ không phải viết lại cả màn. Cái đổi nằm bên dưới: có cache, có gộp lời
 * gọi trùng, và `invalidate` làm mất hiệu lực theo KHOÁ chứ không theo trí nhớ.
 */

/**
 * Khoá truy vấn — khai TẬP TRUNG, không gõ chuỗi tại chỗ.
 *
 * ⚠ Gõ `['customers']` ở màn này và `['customer']` ở màn kia là hai kho cache khác nhau, và
 *   `invalidate` của bên này không đụng tới bên kia. Lỗi đó KHÔNG có thông báo nào: màn vẫn chạy,
 *   chỉ là dữ liệu cũ. Khai ở một chỗ thì `tsc` bắt được lỗi gõ sai.
 */
export const qk = {
  customers: () => ['customers'] as const,
  vendors: () => ['vendors'] as const,
  /* ══ BẢNG DANH MỤC — lọc và cắt trang ở MÁY CHỦ (§8.3) ═════════════════════════════════════
     ⚠ Khoá PHẢI mang bộ lọc: đổi ô 🔍 hay bấm ô KPI là một TẬP DÒNG KHÁC, không phải cùng tập vẽ
       lại. Dùng chung một khoá thì trang 2 của bộ lọc cũ hiện ra dưới bộ lọc mới.
     ⚠ KHÔNG nhét con trỏ / cỡ trang vào đây — `useServerPager` tự nối chúng vào đuôi khoá. */
  customerBoard: (filters: string) => ['customer-board', filters] as const,
  customerBoardSummary: (filters: string) => ['customer-board-summary', filters] as const,
  vendorBoard: (filters: string) => ['vendor-board', filters] as const,
  vendorBoardSummary: (filters: string) => ['vendor-board-summary', filters] as const,
  users: () => ['users'] as const,
  appUsers: () => ['app-users'] as const,
  vendorUsers: () => ['vendor-users'] as const,
  /** Tình trạng mật khẩu CỦA CHÍNH MÌNH (E13 sửa 19/10) — thẻ 🔒 Mật khẩu, hộp 🔒 ở đầu trang, màn ép đổi. */
  passwordState: () => ['password-state'] as const,
  approvalRules: () => ['approval-rules'] as const,
  quotes: () => ['quotes'] as const,
  quotesOf: (customerId: string) => ['quotes', 'customer', customerId] as const,
  quoteTemplates: (activeOnly: boolean) => ['quote-templates', activeOnly] as const,
  orderPrices: (query: string) => ['order-prices', query] as const,
  creditExtensions: () => ['credit-extensions'] as const,
  notifs: () => ['notifs'] as const,
  outbox: (customerId: string) => ['outbox', customerId] as const,
  onBehalfRecvs: (status: string) => ['onbehalf-recvs', status] as const,
  cashPlan: () => ['cash-plan'] as const,
  vendorPostings: (vendorId: string) => ['vendor-postings', vendorId] as const,
  orders: () => ['orders'] as const,
  /* ══ BẢNG ĐƠN HÀNG LỌC Ở MÁY CHỦ (đợt Đ4) — MỌI khoá dưới tiền tố `['orders', …]`: các chỗ đang làm mới
     `qk.orders()` sau khi ghi (BillCell, QuotesTab, StatementsTab…) chạm tới cả trang · tóm tắt · dòng đọc lại.
     ⚠ Khoá mang BỘ LỌC (đã trễ) dạng chuỗi — xem ghi chú ở `customerBoard`. */
  orderBoard: (filters: string) => ['orders', 'bang', filters] as const,
  orderBoardSummary: (filters: string) => ['orders', 'bang-tom-tat', filters] as const,
  /** Đọc LẠI đúng các đơn này (đang tick · đang mở chi tiết) — cửa duy nhất thấy cả đơn hệ cũ. */
  orderBoardRows: (ids: string) => ['orders', 'bang-ids', ids] as const,
  orderProvinces: () => ['orders', 'tinh-da-dung'] as const,
  /** Số đơn trong phạm vi xem (dòng xanh "👤 chỉ xem đơn của mình" — `RtNotices`). */
  orderScopeCount: () => ['orders', 'so-don-pham-vi'] as const,
  legs: (jobId: string) => ['legs', jobId] as const,
  legsRange: (from: string, to: string) => ['legs-range', from, to] as const,
  /** Giá vốn từng chặng của MỘT ngày — khoá kèm danh sách đơn để đổi ngày là nạp lại đúng tập. */
  legCosts: (day: string, jobIds: string) => ['leg-costs', day, jobIds] as const,
  priceAudit: (from: string, to: string) => ['price-audit', from, to] as const,
  listPriceRows: (versionId: string) => ['list-price-rows', versionId] as const,
  myLegs: () => ['my-legs'] as const,
  myVendorCard: () => ['my-vendor-card'] as const,
  myPlanned: () => ['my-planned'] as const,
  attachments: (kind: string, id: string) => ['attachments', kind, id] as const,
  costs: (jobId: string) => ['costs', jobId] as const,
  /** 💰 Hoa hồng CỦA CHÍNH người đang đăng nhập — cửa hẹp `0126`, khác hẳn bảng hoa hồng chung. */
  myCommission: (ym: string) => ['my-commission', ym] as const,
  /** 💸 Phiếu ĐNTT chạm tới khoản chi phí của MỘT đơn — hộp chi ngay tại màn đơn (`0127`). */
  jobPayRequests: (jobId: string) => ['job-pay-requests', jobId] as const,
  billQueue: () => ['bill-queue'] as const,
  invoices: (q: string) => ['invoices', q] as const,
  invoice: (id: string) => ['invoice', id] as const,
  arPayments: (id: string) => ['ar-payments', id] as const,
  /** Cọc / khách trả trước — khoá kèm bộ lọc để đổi khách là nạp lại đúng danh sách. */
  arAdvances: (customerId: string, openOnly: boolean) =>
    ['ar-advances', customerId, openOnly] as const,
  debt: (asOf: string) => ['debt', asOf] as const,
  debtOf: (customerId: string, asOf: string) => ['debt', asOf, customerId] as const,
  apDebtOf: (vendorId: string, asOf: string) => ['ap-debt', asOf, vendorId] as const,
  customerOrders: (customerId: string) => ['orders', 'customer', customerId] as const,
  statements: (q: string) => ['statements', q] as const,
  statementPrint: (id: string) => ['statement-print', id] as const,
  issueHints: (mgmtOnly: boolean) => ['ar-issue-hints', mgmtOnly] as const,
  /** Gắn theo bảng kê đang xuất: lần xuất trước vừa dùng số đó thì lần mở sau không đọc kết quả cũ "chưa dùng". */
  arDocNoTaken: (statementId: string, docNo: string) => ['ar-doc-no-taken', statementId, docNo] as const,
  /** Số phiếu dự kiến của hộp lập phiếu tiền — đủ mọi thứ quyết định dãy số, và số gõ tay để kiểm trùng. */
  voucherPreview: (accountId: string, type: string, txnDate: string, mgmtOnly: boolean, voucherNo: string) =>
    ['voucher-preview', accountId, type, txnDate, mgmtOnly, voucherNo] as const,
  railPriceBoard: () => ['rail-price-board'] as const,
  statement: (id: string) => ['statement', id] as const,
  billCandidates: () => ['bill-candidates'] as const,
  billCandidatesOf: (customerId: string) => ['bill-candidates', customerId] as const,
  onBehalf: (from: string, to: string) => ['on-behalf', from, to] as const,
  apWaiting: () => ['ap-waiting'] as const,
  apStatements: (q: string) => ['ap-statements', q] as const,
  apInvoices: (q: string) => ['ap-invoices', q] as const,
  apDocRegister: () => ['ap-doc-register'] as const,
  onBehalfBoard: () => ['onbehalf-board'] as const,
  vendorStmts: (q: string) => ['vendor-stmts', q] as const,
  vendorStmtsAll: () => ['vendor-stmts'] as const,
  vendorStmtWarnings: () => ['vendor-stmt-warnings'] as const,
  vendorStmt: (key: string) => ['vendor-stmt', key] as const,
  arDocRegister: () => ['ar-doc-register'] as const,
  /** Sổ chứng từ BÁN — khoá mang cả bộ lọc (xem ghi chú ở `customerBoard`). */
  arDocPage: (filters: string) => ['ar-doc-page', filters] as const,
  arDocSummary: (filters: string) => ['ar-doc-summary', filters] as const,
  arDoc: (id: string) => ['ar-doc', id] as const,
  arHints: () => ['ar-hints'] as const,
  apDoc: (id: string) => ['ap-doc', id] as const,
  jobFeePending: (q: string) => ['job-fee-pending', q] as const,
  jobFeePendingAll: () => ['job-fee-pending'] as const,
  jobFeeStatements: () => ['job-fee-statements'] as const,
  /** 👁 Xem trước ghi sổ hàng loạt (L47) — theo tập bảng kê + ngày ghi sổ. */
  jobFeeBulkPostPreview: (ids: string, postDate: string) => ['job-fee-bulk-post-preview', ids, postDate] as const,
  apInvoice: (id: string) => ['ap-invoice', id] as const,
  apDebt: (asOf: string) => ['ap-debt', asOf] as const,
  apRecon: (q: string) => ['ap-recon', q] as const,
  amisDocs: (q: string) => ['amis-docs', q] as const,
  amisDoc: (id: string) => ['amis-doc', id] as const,
  /** Còn chứng từ CHỜ ĐẨY không (nháp + đã soát) — thẻ 🧾 ở `/quan-tri`. Chung tiền tố `amis-docs` để mọi lượt làm mới danh sách kéo theo. */
  amisReadyAny: () => ['amis-docs', 'ready-any'] as const,
  amisWaiting: () => ['amis-waiting'] as const,
  stats: (from: string, to: string, basis = 'plan') => ['stats', from, to, basis] as const,
  dashOverview: (from: string, to: string, basis: string) => ['dash-overview', from, to, basis] as const,
  dashYearChart: (year: number) => ['dash-year-chart', year] as const,
  contFleet: () => ['co-fleet'] as const,
  planPeriods: () => ['plan-periods'] as const,
  planReport: (id: string) => ['plan-report', id] as const,
  contOrders: () => ['co-orders'] as const,
  contCatalog: () => ['co-catalog'] as const,
  fuelStock: () => ['fuel-stock'] as const,
  fuelMoves: () => ['fuel-moves'] as const,
  contRepairs: () => ['cont-repairs'] as const,
  contReport: (from: string, to: string) => ['cont-report', from, to] as const,
  contProfit: (year: number, basis: string, month: string) =>
    ['cont-profit', year, basis, month] as const,
  reeferJobs: () => ['reefer-jobs'] as const,
  contMaterials: () => ['cont-materials'] as const,
  reeferBoard: () => ['reefer-board'] as const,
  contRelatedCosts: () => ['cont-related-costs'] as const,
  repairFiles: (id: string) => ['repair-files', id] as const,
  fuelLogs: () => ['fuel-logs'] as const,
  fuelTransfers: () => ['fuel-transfers'] as const,
  fuelStockMonth: (month: string) => ['fuel-stock-month', month] as const,
  fuelYear: (year: number) => ['fuel-year', year] as const,
  fuelByJob: (year: number, month: number) => ['fuel-by-job', year, month] as const,
  deprAlloc: () => ['depr-alloc'] as const,
  fuelReminder: () => ['fuel-reminder'] as const,
  expenseItems: () => ['expense-items'] as const,
  bankAccounts: () => ['bank-accounts'] as const,
  bidRequests: () => ['bid-requests'] as const,
  bidRequest: (id: string) => ['bid-request', id] as const,
  myBids: () => ['my-bids'] as const,
  commissionRuns: () => ['commission-runs'] as const,
  commissionRun: (id: string) => ['commission-run', id] as const,
  commissionPolicies: () => ['commission-policies'] as const,
  custPolicies: () => ['cust-policies'] as const,
  custPolicyMatch: (query: string) => ['cust-policy-match', query] as const,
  /** Mọi kết quả tra chính sách — làm tươi sau khi duyệt / từ chối / lập tờ trình. */
  custPolicyMatches: () => ['cust-policy-match'] as const,
  orderFeeLocks: (orderId: string) => ['order-fee-locks', orderId] as const,
  commissionHolds: () => ['commission-holds'] as const,
  commissionPayouts: () => ['commission-payouts'] as const,
  railRateBatches: () => ['rail-rate-batches'] as const,
  railCostRates: () => ['rail-cost-rates'] as const,
  railPriceExceptions: () => ['rail-price-exceptions'] as const,
  railStatements: () => ['rail-statements'] as const,
  offsets: (q: string) => ['offsets', q] as const,
  offsetCandidates: () => ['offset-candidates'] as const,
  closedLoop: (from: string, to: string, basis: string) => ['closed-loop', from, to, basis] as const,
  dunning: (customerId: string, asOf: string) => ['dunning', customerId, asOf] as const,
  bankTxns: (q: string) => ['bank-txns', q] as const,
  bankSummary: (from: string, to: string) => ['bank-summary', from, to] as const,
  cashFlow: (from: string, to: string) => ['cash-flow', from, to] as const,
  bankForecast: (asOf: string, days: number) => ['bank-forecast', asOf, days] as const,
  /** Bộ rà sổ tiền đọc CẢ SỔ, không theo kỳ — nên khoá không mang kỳ. Xem `LedgerAuditPanels`. */
  ledgerAudit: () => ['ledger-audit'] as const,
  misaSheet: (kind: string, from: string, to: string, pending: boolean) => ['misa-sheet', kind, from, to, pending] as const,
  misaObjects: (range: string, from: string, to: string) => ['misa-objects', range, from, to] as const,
  pairAudit: () => ['pair-audit'] as const,
  myApInvoices: () => ['my-ap-invoices'] as const,
  myRecon: () => ['my-recon'] as const,
  payRequests: (q: string) => ['pay-requests', q] as const,
  paySteps: () => ['pay-steps'] as const,
  payRequest: (id: string) => ['pay-request', id] as const,
  payPosting: (id: string) => ['pay-request', id, 'posting'] as const,
  payBasis: (apInvoiceId: string) => ['pay-basis', apInvoiceId] as const,
  saleEmails: () => ['ar-sale-emails'] as const,
  ledger131: (customerId: string) => ['ar-ledger-131', customerId] as const,
  payRequestDocs: (id: string) => ['pay-request', id, 'docs'] as const,
  payRequestLoans: (id: string) => ['pay-request', id, 'loans'] as const,
  payRequestStatement: (id: string) => ['pay-request', id, 'statement'] as const,
  payable: (vendorId: string) => ['payable', vendorId] as const,
  payDocs: (q: string) => ['pay-docs', q] as const,
  payDoc: (id: string) => ['pay-doc', id] as const,
  /**
   * ⚠ PHẢI CÓ `side` TRONG KHOÁ. Hai tab Danh mục cùng gọi cửa hợp đồng với `partyId` rỗng nhưng
   *   KHÁC PHÍA (bán ↔ mua). Khoá chỉ gồm `partyId` thì hai tab dùng chung MỘT ô cache: tab nào
   *   mở trước nạp phía của mình, tab kia đọc lại đúng danh sách đó và bốn ô KPI của nó đếm nhầm
   *   sổ của phía bên kia — không lỗi, không cảnh báo, chỉ sai số.
   */
  contracts: (side: 'customer' | 'vendor', partyId = '') => ['contracts', side, partyId] as const,
  contractPrices: (side: string, id: string) => ['contract-prices', side, id] as const,
  docTemplates: () => ['doc-templates'] as const,
  companySettings: () => ['company-settings'] as const,
  acc: (kind: string) => ['acc', kind] as const,
  ledgerLock: () => ['ledger-lock'] as const,
  dispatchPlan: (vendorId: string, jobIds: string, nLeg: number, editVendorId = '') => ['dispatch-plan', vendorId, jobIds, nLeg, editVendorId] as const,
  legEditState: (jobId: string) => ['leg-edit-state', jobId] as const,
  opsBoard: (key: string) => ['ops-board', key] as const,
  opsItems: (kind: string) => ['ops-items', kind] as const,
  priceCats: () => ['price-cats'] as const,
  allocBoard: (kind: string) => ['alloc-board', kind] as const,
  fuelPrices: () => ['fuel-prices'] as const,
  khthGrid: (year: number, dim: string) => ['khth-grid', year, dim] as const,
  khthReport: (year: number) => ['khth-report', year] as const,
  bctcYear: (year: number) => ['bctc-year', year] as const,
  priceCatLines: (catId: string) => ['price-cat-lines', catId] as const,
  priceVersions: () => ['price-versions'] as const,
  /** 🗑 Bản rà mọi phiên cho hộp "Quản lý phiên" — khoá riêng: chỉ ba vai quản lý gọi được, đừng dùng chung với `priceVersions`. */
  priceVersionAudit: () => ['price-version-audit'] as const,
  priceStats: (extra: string) => ['price-stats', extra] as const,
  usableVersions: (onDate?: string) => ['usable-versions', onDate ?? ''] as const,
} as const

export interface ListState<T> {
  /** `null` = CHƯA lấy xong. Mảng rỗng = đã lấy xong và không có dòng nào. Hai thứ khác nhau. */
  rows: T[] | null
  err: string | null
  reload: () => void
}

/**
 * Danh sách từ máy chủ.
 *
 * ⚠ `rows` là `T[] | null` chứ không phải `T[]`: màn phân biệt "đang tải" với "không có dòng nào"
 *   bằng chính chỗ này. Trả `[]` khi đang tải là hiện dòng chữ "Chưa có dữ liệu" trong lúc dữ liệu
 *   đang trên đường về — hành vi đã có ở bản cũ và không được làm mất.
 */
export const useList = <T>(
  key: readonly unknown[],
  fetcher: () => Promise<T[]>,
  opts?: { enabled?: boolean },
): ListState<T> => {
  const client = useQueryClient()
  const q = useQuery({
    queryKey: key,
    queryFn: fetcher,
    enabled: opts?.enabled ?? true,
  })
  // `key` là mảng hằng được dựng LẠI mỗi lần render (`qk.customers()` trả mảng mới), nên so theo
  // THAM CHIẾU sẽ dựng lại `reload` ở mọi lần render và kéo theo mọi `useEffect` phụ thuộc nó.
  // So theo NỘI DUNG bằng một khoá chuỗi.
  const keyId = JSON.stringify(key)
  const reload = useCallback(
    () => { void client.invalidateQueries({ queryKey: JSON.parse(keyId) as unknown[] }) },
    [client, keyId],
  )
  return {
    rows: q.data ?? null,
    err: q.error === null ? null : messageOf(q.error),
    reload,
  }
}

/**
 * Một đối tượng ĐƠN LẺ từ máy chủ (khác `useList`, vốn trả một mảng).
 *
 * ⚠ Sinh ra ở F14 cho cửa chi phí, và lý do nằm ở chỗ cửa đó trả `{ rows, summary }`: TỔNG HỢP do
 *   MÁY CHỦ cộng, không phải màn tự cộng từ `rows`. Dùng `useList` ở đây sẽ vứt mất `summary`, và
 *   màn sẽ phải cộng lại — mà phép cộng đó có hai khoản (phân bổ dầu · khấu hao vỏ lạnh) KHÔNG
 *   nằm trong `rows`. Bản cũ đã lệch số đúng theo cách đó.
 */
export interface OneState<T> {
  /** `null` = CHƯA lấy xong. */
  data: T | null
  err: string | null
  reload: () => void
  /**
   * `data` là dữ liệu của khoá TRƯỚC (đang giữ trên màn nhờ `keepPrevious`) trong lúc khoá mới còn đang tải. Màn nào
   * thao tác trên "các dòng đang hiện" (ô tick cả trang) phải khoá thao tác đó trong lúc này — các dòng đang thấy
   * không còn là trang được chọn.
   */
  stale: boolean
}

export const useOne = <T>(
  key: readonly unknown[],
  fetcher: () => Promise<T>,
  opts?: { enabled?: boolean; keepPrevious?: boolean },
): OneState<T> => {
  const client = useQueryClient()
  /* ⚠ `keepPrevious` — GIỮ DỮ LIỆU CŨ TRÊN MÀN trong lúc lấy khoá mới, thay vì rơi về `null`.
     Cần cho mọi màn lật trang / lọc ở máy chủ: không có nó, mỗi lần bấm "Sau ›" hay gõ một ký tự
     vào ô 🔍 là cả bảng biến mất rồi hiện lại — bản cũ lọc tại chỗ nên chưa bao giờ nháy như vậy,
     và §7 đòi màn hình không đổi. */
  const q = useQuery({
    queryKey: key,
    queryFn: fetcher,
    enabled: opts?.enabled ?? true,
    ...(opts?.keepPrevious === true ? { placeholderData: keepPreviousData } : {}),
  })
  const keyId = JSON.stringify(key)
  const reload = useCallback(
    () => { void client.invalidateQueries({ queryKey: JSON.parse(keyId) as unknown[] }) },
    [client, keyId],
  )
  return { data: q.data ?? null, err: q.error === null ? null : messageOf(q.error), reload, stale: q.isPlaceholderData }
}

/**
 * Câu chữ cho người đọc. Lỗi máy chủ đã có tiếng Việt (§10) — dùng nguyên, không viết lại.
 *
 * ⚠ RỚT MẠNG thì KHÔNG có lỗi máy chủ nào: `fetch` ném thẳng `TypeError` với câu tiếng Anh của
 *   trình duyệt ("Failed to fetch" · Safari "Load failed" · Firefox "NetworkError when attempting
 *   to fetch resource"). Hiện nguyên câu đó là chữ Anh thô giữa màn tiếng Việt (soát điện thoại
 *   12/10/2026). Đổi sang câu báo mạng ĐÃ CÓ trong kho (`login.ncc.netError`, nhóm `login` nạp sẵn
 *   lúc khởi động — `STARTUP_KEY_GROUPS`), nên theo đúng ngôn ngữ đang chọn.
 */
const NET_FAIL = /failed to fetch|load failed|networkerror|network request failed/i
export const messageOf = (e: unknown): string => {
  if (e instanceof TypeError && NET_FAIL.test(e.message)) return i18n.t('login.ncc.netError')
  return e instanceof ApiError || e instanceof Error ? e.message : 'Lỗi không xác định.'
}

/**
 * Làm mất hiệu lực cache SAU KHI GHI. Gọi ở nơi ghi, không phải ở nơi đọc.
 *
 * ⚠ Ghi vào một miền thường đụng tới NHIỀU danh sách: sửa khách hàng có thể đổi cả danh sách NCC
 *   (đường đồng bộ KH ↔ NCC). Hàm này nhận nhiều khoá cùng lúc để nơi gọi khai đủ chúng ngay tại
 *   chỗ ghi, thay vì mỗi màn tự nhớ.
 */
export const invalidate = (client: QueryClient, ...keys: readonly (readonly unknown[])[]): void => {
  // `qk.*()` trả mảng `as const` (readonly) — nhận đúng kiểu đó thay vì bắt nơi gọi phải sao chép
  // mảng ra chỉ để bỏ `readonly`.
  for (const key of keys) void client.invalidateQueries({ queryKey: [...key] })
}
